import React, { useRef } from "react";
import { motion } from "framer-motion";
import {
  MessageSquare,
  Clock,
  CheckCircle,
  Smartphone,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const QuickSmsSection = ({onViewPricing}) => {
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  const features = [
    {
      icon: Clock,
      title: "Instant Delivery",
      description: "Messages delivered in seconds to any Nigerian number",
    },
    {
      icon: CheckCircle,
      title: "High Success Rate",
      description: "99.9% delivery success rate with real-time status updates",
    },
    {
      icon: Smartphone,
      title: "Easy Targeting",
      description: "Simple Bulk SMS integration for businesses",
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="overflow-hidden py-20 w-full bg-gradient-to-br from-gray-50 to-white"
    >
      <div className="px-4 mx-auto max-w-8xl">
        <div className="grid grid-cols-1 gap-16 items-center lg:grid-cols-2">
          {/* Content */}
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-800 bg-red-100 rounded-full"
              >
                <MessageSquare className="mr-2 w-4 h-4" />
                Quick SMS
              </motion.div>

              <motion.h2
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-4xl font-bold leading-tight text-gray-900 md:text-5xl"
              >
                Send Messages
                <span className="block text-red-600">Instantly</span>
              </motion.h2>

              <motion.p
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl leading-relaxed text-gray-600"
              >
                Perfect for urgent notifications, OTP codes, and one-time
                messages. Our Quick SMS service ensures your messages reach
                their destination in seconds with guaranteed delivery.
              </motion.p>
            </div>

            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="space-y-6"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ x: -50, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  className="flex items-start space-x-4"
                >
                  <div className="flex flex-shrink-0 justify-center items-center w-12 h-12 bg-red-100 rounded-xl">
                    <feature.icon className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="mb-2 text-lg font-semibold text-gray-900">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-col gap-4 sm:flex-row"
            >
              <motion.button
                onClick={() => navigate("/login")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl"
              >
                Try Quick SMS
              </motion.button>
              <motion.button
                onClick={() => onViewPricing()}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 font-semibold text-red-600 rounded-xl border-2 border-red-500 transition-all duration-300 hover:bg-red-50"
              >
                View Pricing
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Modern Phone Interface */}
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex justify-center lg:justify-end"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="relative mx-auto w-full max-w-md lg:mx-0"
            >
              {/* Phone Frame */}
              <div className="relative mx-auto w-80 h-[36rem] bg-gradient-to-br from-gray-900 to-gray-800 rounded-[3rem] p-2 shadow-2xl">
                {/* Screen */}
                <div className="relative w-full h-full bg-black rounded-[2.5rem] overflow-hidden flex flex-col">
                  {/* Status Bar */}
                  <div className="flex flex-shrink-0 justify-between items-center px-6 py-3 bg-gradient-to-r from-red-500 to-red-600">
                    <span className="text-sm font-medium text-white">9:41</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-1 h-1 bg-white rounded-full"></div>
                      <div className="w-1 h-1 bg-white rounded-full"></div>
                      <div className="w-1 h-1 bg-white rounded-full"></div>
                    </div>
                  </div>

                  {/* SMS Platform Interface */}
                  <div className="flex flex-col flex-1 bg-white">
                    {/* Header */}
                    <div className="flex flex-shrink-0 items-center px-4 py-3 bg-gradient-to-r from-red-500 to-red-600">
                      <div className="flex justify-center items-center w-10 h-10 rounded-full bg-white/20">
                        <MessageSquare className="w-5 h-5 text-white" />
                      </div>
                      <div className="ml-3">
                        <h3 className="font-semibold text-white">Quick SMS</h3>
                        <p className="text-sm text-red-100">
                          Send messages instantly
                        </p>
                      </div>
                    </div>

                    {/* SMS Form */}
                    <div className="flex-1 p-4 space-y-4">
                      {/* Recipient Field */}
                      <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="space-y-2"
                      >
                        <label className="text-sm font-medium text-gray-700">
                          Recipient Number
                        </label>
                        <div className="px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-600">
                            +233 801 234 5678
                          </p>
                        </div>
                      </motion.div>

                      {/* Message Field */}
                      <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="space-y-2"
                      >
                        <label className="text-sm font-medium text-gray-700">
                          Message
                        </label>
                        <div className="px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 min-h-[80px]">
                          <p className="text-sm text-gray-600">
                            Your OTP code is: 123456
                          </p>
                        </div>
                      </motion.div>

                      {/* Send Button */}
                      <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        className="pt-2"
                      >
                        <button className="px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
                          Send Message
                        </button>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Success Indicators */}
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 1 }}
                className="absolute -top-4 -right-4"
              >
                <div className="flex items-center px-3 py-2 text-sm text-white bg-red-500 rounded-full shadow-lg">
                  <CheckCircle className="mr-2 w-4 h-4" />
                  Delivered
                </div>
              </motion.div>

              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="absolute -bottom-4 -left-4"
              >
                <div className="flex items-center px-3 py-2 text-sm text-white bg-blue-500 rounded-full shadow-lg">
                  <Zap className="mr-2 w-4 h-4" />
                  Instant
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default QuickSmsSection;
